import React from "react";
import { vehicleConfig } from "../../data/vehicleData"; // Adjust path as needed

// --- Icons Helper Component ---
const Icon = ({ name, className = "w-4 h-4" }) => {
  switch (name) {
    case "user":
      return (
        <svg
          className={className}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      );
    case "bag":
      return (
        <svg
          className={className}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      );
    case "cog":
      return (
        <svg
          className={className}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      );
    case "gas":
      return (
        <svg
          className={className}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          />
        </svg>
      );
    case "check":
      return (
        <svg
          className={className}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    default:
      return null;
  }
};

// --- Single Card Component ---
function VehicleCard({ vehicle }) {
  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-lg p-4">
      {/* Vehicle Image */}
      <div className="-mx-4 -mt-4 mb-4 h-48 overflow-hidden bg-slate-100">
        <img
          src={vehicle.image}
          alt={vehicle.title}
          className="h-full w-full object-cover object-center"
        />
      </div>

      {/* Header Info */}
      <span className="mb-2 inline-block rounded bg-[#f0f4fb] px-2 py-1 text-[10px] font-extrabold uppercase tracking-widest text-[#081c3a] w-fit">
        {vehicle.category}
      </span>
      <h3 className="mb-1 text-xl font-extrabold text-[#081c3a]">
        {vehicle.title}
      </h3>
      <p className="mb-5 text-sm text-slate-500">{vehicle.subtitle}</p>

      {/* Specs Grid */}
      <div className="mb-5 grid grid-cols-2 gap-2">
        <div className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 text-xs font-bold text-[#081c3a]">
          <Icon name="user" className="h-4 w-4 text-slate-400" />{" "}
          {vehicle.specs.passengers} Passengers
        </div>
        <div className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 text-xs font-bold text-[#081c3a]">
          <Icon name="bag" className="h-4 w-4 text-slate-400" />{" "}
          {vehicle.specs.bags} Bags
        </div>
        <div className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 text-xs font-bold text-[#081c3a]">
          <Icon name="cog" className="h-4 w-4 text-slate-400" />{" "}
          {vehicle.specs.transmission}
        </div>
        <div className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 text-xs font-bold text-[#081c3a]">
          <Icon name="gas" className="h-4 w-4 text-slate-400" />{" "}
          {vehicle.specs.fuel}
        </div>
      </div>

      {/* Feature Tags */}
      <div className="mb-6 flex flex-wrap gap-2">
        {vehicle.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-red-200 bg-red-50 px-2 py-1 text-[9px] font-extrabold uppercase tracking-wide text-[#e8211b]"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Push everything below to the bottom if content above is short */}
      <div className="mt-auto">
        {/* Price Box */}
        <div className="rounded-xl border border-slate-200 p-4 text-center">
          <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Example Starting Rate
          </p>
          <div className="mb-1 flex items-baseline justify-center text-[#e8211b]">
            <span className="text-xl font-bold">$</span>
            <span className="text-3xl font-black">{vehicle.price}</span>
            <span className="text-sm font-bold text-slate-500">/day*</span>
          </div>
          <p className="text-[9px] text-slate-400">
            *Rates vary by location, vehicle availability and travel dates.
          </p>
        </div>

        {/* CTA Button */}
        <button className="mt-4 mb-4 w-full rounded-full bg-[#e8211b] py-3 text-sm font-extrabold uppercase tracking-wide text-white transition-colors hover:bg-red-700 shadow-md">
          Find Your Rental Car
        </button>

        {/* Footer Features */}
        <div className="flex items-center justify-between px-1 text-[10px] font-bold text-slate-500">
          <div className="flex flex-col items-center gap-1 text-center">
            <Icon name="check" className="h-3 w-3 text-slate-400" /> Clear
            Details
          </div>
          <div className="flex flex-col items-center gap-1 text-center">
            <Icon name="check" className="h-3 w-3 text-slate-400" /> Booking
            Assistance
          </div>
          <div className="flex flex-col items-center gap-1 text-center">
            <Icon name="check" className="h-3 w-3 text-slate-400" /> No Hidden
            Fees
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Main Categories Component ---
export default function VehicleCategories() {
  return (
    <section className="mx-auto max-w-[1580px] px-4 py-16 md:px-8">
      {/* Header Section */}
      <div className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-[#e8211b]">
            <span className="mr-2 text-[16px] leading-none">•</span> Vehicle
            Categories
          </p>
          <h2 className="text-4xl font-black leading-tight text-[#081c3a] md:text-5xl lg:text-[54px]">
            Explore Vehicles for Every Kind of Trip
          </h2>
        </div>
        <div className="max-w-md rounded-2xl bg-[#f2f6fa] p-5 lg:p-6 text-[15px] leading-relaxed text-[#3a4759] border border-slate-100">
          From compact cars to spacious SUVs, browse rental options suited for
          business travel, weekend getaways, family trips, and more.
        </div>
      </div>

      {/* Grid of Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {vehicleConfig.map((vehicle) => (
          <VehicleCard key={vehicle.id} vehicle={vehicle} />
        ))}
      </div>
    </section>
  );
}
